import React, { useEffect, useState } from "react";
import AuthIcon from "pages/AuthenticationInner/AuthIcon";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, resetRegisterFlag } from "slices/thunk";
import { createSelector } from 'reselect';
import { Link, useNavigate } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik as useFormic } from "formik";

// Image
import logo from "assets/images/SOILOGO.png";
import { RootState } from "slices";
import { WithTranslationProps } from "Common/Components/Translation/WithTranslationProps";
import withTranslation from "Common/withTranslation";
import { RegisterViewModel } from "Common/ViewModels/Authentication/RegisterViewModel";
import { RegisterErrorViewModel } from "Common/ViewModels/Errors/Authentication/RegisterErrrorViewModel";
import withNotifications, { WithNotificationsProps } from "Common/withNotifications";
import withMessages, { WithMessagesProps } from "Common/withMessages";
import { axios_helper_async } from "helpers/Axios";
import { MessageType } from "Common/Components/Messages/messageTypes";
import VerificationInput from "Common/Components/VerificationInput/VerificationInput";
import { NotificationType } from "Common/Components/Notifications/notificationTypes";
import LoadingButton from "Common/Components/LoadingOverlay/LoadingButton/LoadingButton";

const Register: React.FC<WithTranslationProps & WithNotificationsProps & WithMessagesProps> = ({
    t,
    messages,
    notifications
}) => {
    const initialModel: RegisterViewModel = {
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        password2: ''
    }
    const modelFields: Record<keyof RegisterViewModel, string> = {
        email: 'email',
        firstName: 'firstName',
        lastName: 'lastName',
        password: 'password',
        password2: 'password2'
    }    
    const [model, setModel] = useState<RegisterViewModel>(initialModel);
    const [errors, setErrors] = useState<RegisterErrorViewModel>({});
    const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
    const [isSecurePassword, setIsSecurePassword] = useState<boolean>(false);
    const [isPolicy, setIsPolicy] = useState<boolean>(false);
    const [isRegistable, setIsRegistable] = useState<boolean>(false);
    const [isVerifyingCode, setIsVerifyingCode] = useState<boolean>(false);
    const [verificationCode, setVerificationCode] = useState<string>("");
    const [verificationInput, setVerificationInput] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }));
    }

    const handleChangePolicy = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;

        setIsPolicy(checked);
    }

    // Función para validar el correo electrónico usando regex
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

    const validModel = () => {
        let newErrors: RegisterErrorViewModel = {};
        if(!model.email || !validateEmail(model.email)) newErrors.email = "Register.Errors.Email"
        if(!model.firstName) newErrors.firstName = "Register.Errors.FirstName"
        if(!model.lastName) newErrors.lastName = "Register.Errors.LastName"
        if(!model.password) newErrors.password = "Register.Errors.Password"
        if(!model.password2) newErrors.password2 = "Register.Errors.Password"
        if(!isPolicy) newErrors.policy = "Register.Errors.Policy"

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    }

    const verifyPasswords = () => {
        setIsValidPassword(
            model.password !== null && 
            model.password !== undefined && 
            model.password !== '' && 
            model.password2 !== null && 
            model.password2 !== undefined && 
            model.password2 !== '' && 
            model.password === model.password2
        );
    }

    const handleRegister = async () => {
        try {
            if(validModel()) { 
                setIsLoading(true);
                const response = await axios_helper_async<boolean>("/Account/Register", 'POST', model);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if(response.isSuccess) {
                    if(response.data) {
                        window.location.href = "/login";
                    }
                    else {
                        messages.showMessage("Register.Errors.Register", MessageType.Error, "Register.Errors.RegisterTitle");
                    }
                }
                else {
                    messages.showMessage("AxiosAPI.FailedFetch", MessageType.Error, "Error");
                }
            }
        }
        catch(err: any) {
            messages.showMessage("AxiosAPI.UnexpectedError", MessageType.Error, "Error");
        }
        finally{
            setIsLoading(false);
        }
    }

    const handleVerifyRegister = async () => {
        try {
            if(validModel()) {
                const body = {
                    email: model.email
                }
                const response = await axios_helper_async<boolean>("/Account/Verify-Email", 'GET', body);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if(response.isSuccess) {
                    if(!response.data) {
                        setIsRegistable(true);
                    }
                    else {
                        messages.showMessage("Register.Errors.Registable", MessageType.Error, "Register.Email");
                    }
                }
                else {
                    messages.showMessage("AxiosAPI.FailedFetch", MessageType.Error, "Error");
                }
            }
        }
        catch(err: any) {
            messages.showMessage("AxiosAPI.UnexpectedError", MessageType.Error, "Error");
        }
    }

    const handleGetVerificationCode = async () => {
        try {
            if(validModel()) {
                const body = {
                    email: model.email
                }

                const response = await axios_helper_async<string>("/Account/Send-Code-Verification", 'GET', body);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if(response.isSuccess) {
                    if(response.data) {
                        // console.log(response.data);
                        setIsVerifyingCode(true);
                        setVerificationCode(response.data);
                        notifications.showNotification("VerificationCode.CodeSent", NotificationType.Information);
                    }
                    else {
                        messages.showMessage("Register.Errors.VerifyCode", MessageType.Error, "Register.Email");
                    }
                }
                else {
                    messages.showMessage("AxiosAPI.FailedFetch", MessageType.Error, "Error");
                }
            }
        }
        catch(err: any) {
            messages.showMessage("AxiosAPI.UnexpectedError", MessageType.Error, "Error");
        }
    }

    const handleOnComplete = async () => {
        await handleRegister();
    }

    useEffect(() => {
        verifyPasswords();
    }, [model.password, model.password2])

    useEffect(() => {
        if(isRegistable && !isVerifyingCode) {
            (async () => {
                await handleGetVerificationCode();
            })();
        }
    }, [isRegistable])

    useEffect(() => {

    }, [isVerifyingCode])

    return (
        <React.Fragment>
            {!isVerifyingCode ? 
            <div className="mb-0 w-screen lg:mx-auto lg:w-[500px] card shadow-lg border-none shadow-slate-100 relative">
                <div className="!px-10 !py-12 card-body">
                    <Link to="/">
                        <img src={logo} alt="" className="block h-10 mx-auto" />
                    </Link>

                    <div className="mt-8 text-center">
                        <h4 className="mb-1 text-custom-500 dark:text-custom-500">{t('Register.Title')}</h4>
                    </div>

                    <form action="/" className="mt-10">
                        <div className="mb-3">
                            <label htmlFor="email-field" className="inline-block mb-2 text-base font-medium">{t('Register.Email')}</label>
                            <input
                                type="email"
                                id={modelFields.email}
                                name={modelFields.email}
                                className={`form-input ${errors.email ? "border border-red-500" : null}`}
                                placeholder={t("Register.EmailPlaceholder")}
                                value={model.email}
                                onChange={handleChange}
                            />
                            {errors.email ? (
                                <div id="email-error" className="mt-1 text-sm text-red-500">{t(errors.email)}</div>
                            ) : null}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="username-field" className="inline-block mb-2 text-base font-medium">{t('Register.FirstName')}</label>
                            <input
                                type="text"
                                id={modelFields.firstName}
                                name={modelFields.firstName}
                                className={`form-input ${errors.firstName ? "border border-red-500" : null}`}
                                placeholder={t("Register.FirstNamePlaceholder")}
                                value={model.firstName}
                                onChange={handleChange}
                            />
                            {errors.firstName ? (
                                <div id="username-error" className="mt-1 text-sm text-red-500">{t(errors.firstName)}</div>
                            ) : null}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="username-field" className="inline-block mb-2 text-base font-medium">{t('Register.LastName')}</label>
                            <input
                                type="text"
                                id={modelFields.lastName}
                                name={modelFields.lastName}
                                className={`form-input ${errors.lastName ? "border border-red-500" : null}`}
                                placeholder={t("Register.LastNamePlaceholder")}
                                value={model.lastName}
                                onChange={handleChange}
                            />
                            {errors.lastName ? (
                                <div id="username-error" className="mt-1 text-sm text-red-500">{t(errors.lastName)}</div>
                            ) : null}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="password" className="inline-block mb-2 text-base font-medium">{t('Register.Password')}</label>
                            <input
                                type="password"
                                id={modelFields.password}
                                name={modelFields.password}
                                className={`form-input ${isValidPassword ? "border border-green-500" : null} ${errors.password ? "border border-red-500" : null}`}
                                placeholder={t("Register.PasswordPlaceholder")}
                                onChange={handleChange}                                
                                value={model.password} 
                            />
                            {errors.password ? (
                                <div id="password-error" className="mt-1 text-sm text-red-500">{t(errors.password)}</div>
                            ) : null}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="password" className="inline-block mb-2 text-base font-medium">{t('Register.Password2')}</label>
                            <input
                                type="password"
                                id={modelFields.password2}
                                name={modelFields.password2}
                                className={`form-input ${isValidPassword ? "border border-green-500" : null} ${errors.password2 ? "border border-red-500" : null}`}
                                placeholder={t("Register.PasswordPlaceholder")}
                                onChange={handleChange}                                
                                value={model.password2} 
                            />
                            {errors.password2 ? (
                                <div id="password-error" className="mt-1 text-sm text-red-500">{t(errors.password2)}</div>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                            <input id="policy" className="w-4 h-4 border rounded-sm appearance-none cursor-pointer bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400" type="checkbox" value="" checked={isPolicy} onChange={handleChangePolicy}/>
                            <label htmlFor="policy" className="align-middle cursor-pointer">
                                {t("Register.AcceptPolicy")}
                            </label>
                        </div>
                        {errors.policy ? (
                                <div id="password-error" className="mt-1 text-sm text-red-500">{t(errors.policy)}</div>
                            ) : null}
                        <p className="italic text-15 text-slate-500 dark:text-zink-200"><a href="#!" className="underline">{t('Register.TermsofUse')}</a></p>
                        {
                            isLoading ?
                                <LoadingButton />
                                :
                            <div className="mt-10">
                                <button type="button" className="w-full btn btn-custom" onClick={handleVerifyRegister}>{t('Register.BtnSignUp')}</button>
                            </div>
                        }
                        <div className="mt-10 text-center">
                            <p className="mb-0 text-slate-500 dark:text-zink-200">{t('Register.AlreadyHaveAccount')} <Link to="/login" className="font-semibold underline transition-all duration-150 ease-linear text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500">{t('Register.SignIn')}</Link> </p>
                        </div>

                    </form>
                </div>
            </div>
            :
            <VerificationInput onComplete={handleOnComplete} onResend={handleGetVerificationCode} verifyCode={verificationCode}></VerificationInput> }
        </React.Fragment>
    );
}

export default withTranslation(withMessages(withNotifications(Register)));